import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from 'rebass/styled-components';
import styled from 'styled-components';
import { space, fontSize } from 'styled-system';
import theme from 'styled-theming';
import { Input, Loading, Button } from '.';

const hintColor = theme('mode', {
  light: (props) => props.theme.colors.lightGrey,
  dark: (props) => props.theme.colors.midLightGrey,
});

const textColor = theme('mode', {
  light: (props) => props.theme.colors.deepGrey,
  dark: (props) => props.theme.colors.lightBlue,
});

const Label = styled.label`
  ${space}
  ${fontSize}
  display: flex;
  align-items: center;
  position: relative;
  color: ${(props) => (props.disabled ? props.theme.colors.midGrey : textColor)};
  left: ${(props) => `${props.labelPosition * 0.9}%`};
  width: ${(props) => (props.labelPosition === 0 ? 'auto' : '50px')}};
`;

const Hint = styled(Box)`
  color: ${hintColor};
  line-height: 1.3;
`;

function InputField(props) {
  const {
    label,
    labelPosition,
    hint,
    disabled,
    isLast,
    isSaving,
    cancel,
    save,
    handleChange,
    required,
    value,
    name,
    id,
    inputType,
    max,
    min,
    handleDrop,
    handleDragOver,
    handleFileUpload,
    placeHolder,
    ...otherProps
  } = props;

  return (
    <Box mb={isLast ? 0 : 3} data-testid="input-field" {...otherProps}>
      {label && (
        <Label
          mb={2}
          fontSize={1}
          fontWeight={400}
          data-testid="input-field-label"
          disabled={disabled}
          labelPosition={labelPosition}
        >
          {label}
          {isSaving && <Loading size={12} ml={2} alignItems="center" />}
        </Label>
      )}
      <Box>
        <Flex>
          <Input
            disabled={disabled}
            handleChange={handleChange}
            required={required}
            value={value}
            name={name}
            id={id}
            inputType={inputType}
            handleDrop={handleDrop}
            handleDragOver={handleDragOver}
            handleFileUpload={handleFileUpload}
            placeHolder={placeHolder}
          />
          {cancel && (
            <Button variant="secondary" ml={2} onClick={cancel} data-testid="cancel-button">
              Cancel
            </Button>
          )}
          {save && (
            <Button variant="primary" ml={2} onClick={save} data-testid="save-button">
              Save
            </Button>
          )}
        </Flex>
        {hint && (
          <Hint mt={2} fontSize={1} fontWeight={300}>
            {hint}
          </Hint>
        )}
      </Box>
    </Box>
  );
}

InputField.defaultProps = {
  handleChange: undefined,
  handleDrop: undefined,
  handleDragOver: undefined,
  handleFileUpload: undefined,
  isSaving: false,
  cancel: undefined,
  save: undefined,
  name: undefined,
  value: undefined,
  hint: undefined,
  disabled: undefined,
  inputType: undefined,
  label: undefined,
  labelPosition: 0,
  isLast: false,
  required: false,
  id: undefined,
  max: undefined,
  min: undefined,
  placeHolder: undefined,
};

InputField.propTypes = {
  label: PropTypes.string,
  labelPosition: PropTypes.number,
  isSaving: PropTypes.bool,
  cancel: PropTypes.func,
  save: PropTypes.func,
  handleChange: PropTypes.func,
  handleDrop: PropTypes.func,
  handleDragOver: PropTypes.func,
  handleFileUpload: PropTypes.func,
  name: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  hint: PropTypes.string,
  disabled: PropTypes.bool,
  inputType: PropTypes.string,
  isLast: PropTypes.bool,
  required: PropTypes.bool,
  id: PropTypes.string,
  max: PropTypes.number,
  min: PropTypes.number,
  placeHolder: PropTypes.string,
};

export default InputField;
